// deploy-bust: 1784133926
/**
 * Cow Guys Farm — UGC Drawer (Dreamland-style)
 * --------------------------------------------
 * Companion to: assets/cg-ugc.js + snippets/cg-ugc-drawer.liquid
 *
 * Owns:
 *  - Listening for `cg-ugc:open-drawer` CustomEvents (dispatched by cg-ugc.js)
 *  - Opening / closing the single shared <dialog data-cg-ugc-drawer>
 *  - Fetching /products/{handle}.js, caching, and populating the product side
 *  - Video playback: muted-by-default autoplay, mute toggle, progress bar
 *    with click-to-seek scrubbing
 *  - Gallery: one image at a time, prev/next arrows, dot indicators
 *  - Variant selection: per-option pills with short value labels (not full
 *    variant titles), price update, ATC variantId tracking
 *  - Add-to-cart: POST /cart/add.js with the cart drawer fallback chain
 *    (cart-notification → cart-drawer → cart:added event → /cart navigate)
 *  - Prev/Next between tiles — wraps at the ends (loops)
 *  - Close: backdrop click + ESC (native <dialog>) + close button
 */

(function () {
  'use strict';

  var productCache = new Map();   // handle -> product JSON
  var triggerCache = {};          // sectionId -> Array<HTMLElement> snapshot
  var state = {
    detail: null,
    product: null,
    variantId: '',
    rootEl: null,
    galleryIndex: 0,
    galleryImages: [],            // Array<string> active product image URLs
    selectedOptions: [],          // current selection per option position
    sellingPlans: [],             // flat list of {id, name, pct} for current product
    subMode: false,               // true when subscribe state active
    sellingPlanId: '',            // currently selected selling_plan id (when subMode)
  };

  // ------------------------------------------------------------------
  // Cart drawer fallback chain
  // ------------------------------------------------------------------
  function handleCartResponse(data) {
    // Prefer the side cart drawer over the smaller cart notification so
    // UGC ATC matches the rest of the site's "open cart drawer" pattern.
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      try { drawer.renderContents(data); return; } catch (_) {}
    }
    var notif = document.querySelector('cart-notification');
    if (notif && typeof notif.renderContents === 'function') {
      try { notif.renderContents(data); return; } catch (_) {}
    }
    var addedEvent = new CustomEvent('cart:added', { bubbles: true, detail: data });
    document.dispatchEvent(addedEvent);
    var bubble = document.querySelector('cart-icon-bubble');
    if (bubble && data && data.sections && data.sections['cart-icon-bubble']) {
      try {
        var html = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        var newBubble = html.querySelector('cart-icon-bubble');
        if (newBubble) bubble.innerHTML = newBubble.innerHTML;
      } catch (_) {}
    }
  }

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------
  function $(root, sel) { return root ? root.querySelector(sel) : null; }

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  function safeText(el, text) {
    if (!el) return;
    el.textContent = text == null ? '' : String(text);
  }

  function clear(el) {
    if (!el) return;
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  // ------------------------------------------------------------------
  // Live card adoption
  //
  // When the page's hero is a cg-pdp product card (sections/cg-pdp.liquid),
  // the drawer shows THAT card: the <section data-cg-pdp> node is moved
  // into [data-cg-ugc-drawer-live] while the drawer is open and moved back
  // to its slot on close. cg-pdp's inline script keeps its `root` closure,
  // so every handler (scent, size, subscribe, ATC) keeps working — nothing
  // is copied or re-initialised. Pages without a cg-pdp hero fall through
  // to the fetch-and-populate card below.
  // ------------------------------------------------------------------
  var live = { card: null, slot: null, lightbox: null, lightboxParent: null };

  function findLiveCard() {
    if (live.card) return live.card;
    var cards = document.querySelectorAll('[data-cg-pdp]');
    for (var i = 0; i < cards.length; i++) {
      if (!cards[i].closest('[data-cg-ugc-drawer]')) return cards[i];
    }
    return null;
  }

  function adoptLiveCard(root) {
    var host = $(root, '[data-cg-ugc-drawer-live]');
    var card = findLiveCard();
    if (!host || !card) return false;
    if (card.parentNode !== host) {
      live.slot = document.createComment('cg-ugc-drawer: live card returns here');
      card.parentNode.insertBefore(live.slot, card);
      host.appendChild(card);
      live.card = card;
      // cg-pdp hoists its zoom lightbox to <body>. A <dialog> renders in the
      // top layer above that, so bring the lightbox along or zoom would
      // open invisibly behind the drawer.
      var lb = document.querySelector('[data-pdp-lightbox]');
      if (lb && !lb.closest('[data-cg-ugc-drawer]')) {
        live.lightbox = lb;
        live.lightboxParent = lb.parentNode;
        host.appendChild(lb);
      }
    }
    host.hidden = false;
    root.classList.add('cg-ugc-drawer--live');
    populatePeekFromCard(root, card);
    return true;
  }

  function restoreLiveCard(root) {
    if (live.card && live.slot && live.slot.parentNode) {
      live.slot.parentNode.replaceChild(live.card, live.slot);
    }
    if (live.lightbox && live.lightboxParent) {
      live.lightboxParent.appendChild(live.lightbox);
    }
    live = { card: null, slot: null, lightbox: null, lightboxParent: null };
    var host = $(root, '[data-cg-ugc-drawer-live]');
    if (host) host.hidden = true;
    if (root) root.classList.remove('cg-ugc-drawer--live');
  }

  // Mobile peek bar (thumb + price + title) read straight off the card.
  function populatePeekFromCard(root, card) {
    var peek = $(root, '[data-cg-ugc-drawer-peek]');
    if (!peek) return;
    var img = card.querySelector('.cg-pdp__mgallery img, .cg-pdp__slide img');
    var title = card.querySelector('.cg-pdp__title');
    var price = card.querySelector('[data-pdp-atc-price], .cg-pdp__price-now');
    var peekImg = $(root, '[data-cg-ugc-drawer-peek-image]');
    var peekPrice = $(root, '[data-cg-ugc-drawer-peek-price]');
    var peekTitle = $(root, '[data-cg-ugc-drawer-peek-title]');
    if (peekImg) { peekImg.src = img ? img.currentSrc || img.src : ''; peekImg.alt = title ? title.textContent : ''; }
    safeText(peekPrice, price ? price.textContent.trim() : '');
    safeText(peekTitle, title ? title.textContent.trim() : '');
    peek.hidden = false;
  }

  // The card's own add-to-cart (Dawn product-form) opens the site cart
  // drawer, which lives outside this <dialog> and would be hidden behind
  // it. Close the UGC drawer as soon as the cart updates so the cart
  // drawer is what the customer sees.
  function wireLiveCardCart(root) {
    var host = $(root, '[data-cg-ugc-drawer-live]');
    if (!host) return;
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
        if (live.card) closeDrawer();
      });
      return;
    }
    host.addEventListener('submit', function () {
      var tries = 0;
      (function poll() {
        var cart = document.querySelector('cart-drawer, cart-notification');
        if (cart && (cart.classList.contains('active') || cart.classList.contains('animate'))) { closeDrawer(); return; }
        if (++tries < 40) setTimeout(poll, 100);
      })();
    }, true);
  }

  // ------------------------------------------------------------------
  // Drawer wiring (idempotent)
  // ------------------------------------------------------------------
  function getDrawer() {
    if (state.rootEl && document.body.contains(state.rootEl)) return state.rootEl;
    var root = document.querySelector('[data-cg-ugc-drawer]');
    if (!root) return null;
    if (root.dataset.cgUgcDrawerInit !== '1') {
      wireDrawer(root);
      wireLiveCardCart(root);
      root.dataset.cgUgcDrawerInit = '1';
    }
    state.rootEl = root;
    return root;
  }

  function wireDrawer(root) {
    var video = $(root, '[data-cg-ugc-drawer-video]');
    var muteBtn = $(root, '[data-cg-ugc-drawer-mute]');
    var progress = $(root, '[data-cg-ugc-drawer-progress]');
    var progressFill = $(root, '[data-cg-ugc-drawer-progress-fill]');
    var closeBtn = $(root, '[data-cg-ugc-drawer-close]');
    var prevBtn = $(root, '[data-cg-ugc-drawer-prev]');
    var nextBtn = $(root, '[data-cg-ugc-drawer-next]');
    var atcBtn = $(root, '[data-cg-ugc-drawer-atc]');
    var galleryPrev = $(root, '[data-cg-ugc-drawer-gallery-prev]');
    var galleryNext = $(root, '[data-cg-ugc-drawer-gallery-next]');

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        // On mobile, the close (X) at the top should collapse the product
        // sheet first if it's open, instead of closing the whole drawer.
        if (root.hasAttribute('data-product-open')) {
          collapseProductSheet(root);
          return;
        }
        closeDrawer();
      });
    }

    // Peek-bar "Shop Now" — slides the product sheet up over the video
    var peekCta = $(root, '[data-cg-ugc-drawer-peek-cta]');
    if (peekCta) {
      peekCta.addEventListener('click', function (e) {
        e.preventDefault();
        expandProductSheet(root);
      });
    }

    // Backdrop click — when click target IS the dialog itself
    root.addEventListener('click', function (event) {
      if (event.target === root) closeDrawer();
    });

    // ESC — native <dialog> dispatches 'cancel'
    root.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeDrawer();
    });

    // Mute toggle
    if (muteBtn && video) {
      muteBtn.addEventListener('click', function () {
        video.muted = !video.muted;
        syncMuteIcon(muteBtn, video.muted);
      });
    }

    // Click the video to pause/resume.
    var playOverlay = $(root, '[data-cg-ugc-drawer-play-overlay]');
    function setPlayState(isPaused) {
      if (playOverlay) playOverlay.hidden = !isPaused;
    }
    if (video) {
      video.addEventListener('click', function () {
        if (video.paused) {
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        } else {
          try { video.pause(); } catch (_) {}
        }
      });
      video.addEventListener('pause', function () { setPlayState(true); });
      video.addEventListener('play', function () { setPlayState(false); });
      video.addEventListener('playing', function () { setPlayState(false); });
      video.addEventListener('ended', function () { setPlayState(true); });
    }
    // Clicking the overlay itself resumes playback.
    if (playOverlay && video) {
      playOverlay.addEventListener('click', function (e) {
        e.stopPropagation();
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      });
    }

    // Progress bar updates
    if (video && progressFill) {
      video.addEventListener('timeupdate', function () {
        if (!video.duration || isNaN(video.duration)) return;
        var pct = (video.currentTime / video.duration) * 100;
        progressFill.style.width = Math.min(100, Math.max(0, pct)) + '%';
      });
      video.addEventListener('ended', function () {
        progressFill.style.width = '0%';
      });
    }

    // Progress bar click-to-seek
    if (progress && video) {
      progress.addEventListener('click', function (event) {
        if (!video.duration || isNaN(video.duration)) return;
        var rect = progress.getBoundingClientRect();
        var clickX = event.clientX - rect.left;
        var ratio = Math.min(1, Math.max(0, clickX / rect.width));
        try { video.currentTime = ratio * video.duration; } catch (_) {}
      });
    }

    // Tile prev / next (loops)
    if (prevBtn) prevBtn.addEventListener('click', function () { navigate(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { navigate(1); });

    // Gallery prev / next
    if (galleryPrev) galleryPrev.addEventListener('click', function () { stepGallery(-1); });
    if (galleryNext) galleryNext.addEventListener('click', function () { stepGallery(1); });

    // "What's Included" details toggle (Hero General pattern)
    var detailsToggle = $(root, '[data-cg-ugc-drawer-details-toggle]');
    var detailsDrawer = $(root, '[data-cg-ugc-drawer-details-drawer]');
    if (detailsToggle && detailsDrawer) {
      detailsToggle.addEventListener('click', function () {
        var open = detailsDrawer.classList.toggle('is-open');
        detailsToggle.classList.toggle('is-open', open);
        detailsToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    // Keyboard arrows inside the dialog cycle between tiles
    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') { event.preventDefault(); navigate(1); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); navigate(-1); }
    });

    // ATC click — same handler for one-time and subscribe ATC buttons.
    // addToCart() reads state.subMode + state.sellingPlanId, so the
    // selling_plan is automatically attached when the subscribe ATC
    // is the trigger.
    if (atcBtn) {
      atcBtn.addEventListener('click', function () {
        addToCart(atcBtn);
      });
    }
  }

  function syncMuteIcon(muteBtn, isMuted) {
    if (!muteBtn) return;
    muteBtn.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
    muteBtn.setAttribute('aria-label', isMuted ? 'Unmute' : 'Mute');
    muteBtn.dataset.muted = isMuted ? 'true' : 'false';
  }

  // ------------------------------------------------------------------
  // Open / close
  // ------------------------------------------------------------------
  function openDrawer(detail) {
    var root = getDrawer();
    if (!root) {
      console.warn('[cg-ugc-drawer] no [data-cg-ugc-drawer] element found in DOM');
      return;
    }
    state.detail = detail || {};
    // Always start with the slide-up sheet collapsed (peek bar visible on
    // mobile). User taps "Shop Now" to expand. Without this, a stuck
    // attribute from a prior session leaves the sheet auto-expanded and
    // the peek bar is never seen.
    root.removeAttribute('data-product-open');
    justOpenedAt = Date.now(); // arm the ghost-click guard

    var video = $(root, '[data-cg-ugc-drawer-video]');
    var muteBtn = $(root, '[data-cg-ugc-drawer-mute]');
    var progressFill = $(root, '[data-cg-ugc-drawer-progress-fill]');
    var productSide = $(root, '[data-cg-ugc-drawer-product]');

    // Reset video — try unmuted first; fall back to muted if browser blocks
    if (video) {
      try { video.pause(); } catch (_) {}
      video.muted = false;
      video.setAttribute('playsinline', '');
      video.setAttribute('loop', '');
      if (state.detail.posterSrc) {
        video.setAttribute('poster', state.detail.posterSrc);
      } else {
        video.removeAttribute('poster');
      }
      if (state.detail.videoSrc) {
        video.src = state.detail.videoSrc;
      } else {
        video.removeAttribute('src');
      }
      try { video.load(); } catch (_) {}
    }
    syncMuteIcon(muteBtn, false);
    if (progressFill) progressFill.style.width = '0%';

    // Clean prior product render before populating new one
    resetProductSide(root);

    // Product side — the page's live cg-pdp card when there is one,
    // otherwise fetch the tile's product into the fallback card.
    if (productSide) {
      if (state.detail.productHandle) {
        productSide.hidden = false;
        if (!adoptLiveCard(root)) loadProduct(state.detail.productHandle, root);
      } else {
        restoreLiveCard(root);
        productSide.hidden = true;
        state.product = null;
        state.variantId = '';
      }
    }

    // Open dialog
    if (!root.open) {
      if (typeof root.showModal === 'function') {
        try { root.showModal(); } catch (_) { root.setAttribute('open', ''); }
      } else {
        root.setAttribute('open', '');
      }
    }

    // Focus first focusable
    setTimeout(function () {
      var focusable = root.querySelector(
        '[data-cg-ugc-drawer-close], [data-cg-ugc-drawer-mute], button, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) { try { focusable.focus(); } catch (_) {} }
    }, 30);

    // Kick off video playback — unmuted first; if browser blocks autoplay
    // with sound, retry muted (and update the mute button icon to match).
    if (video && state.detail.videoSrc) {
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          video.muted = true;
          syncMuteIcon(muteBtn, true);
          var p2 = video.play();
          if (p2 && typeof p2.catch === 'function') p2.catch(function () {});
        });
      }
    }
  }

  function closeDrawer() {
    var root = state.rootEl || document.querySelector('[data-cg-ugc-drawer]');
    if (!root) return;
    var video = $(root, '[data-cg-ugc-drawer-video]');
    if (video) {
      try { video.pause(); } catch (_) {}
      video.removeAttribute('src');
      try { video.load(); } catch (_) {}
    }
    // Reset product side so reopening doesn't accumulate
    resetProductSide(root);
    restoreLiveCard(root);
    // Reset slide-up sheet state so the next open starts collapsed
    root.removeAttribute('data-product-open');
    if (typeof root.close === 'function') {
      try { root.close(); } catch (_) {}
    } else {
      root.removeAttribute('open');
    }
  }

  // iOS Safari fires a synthetic click on whatever element ends up under the
  // user's finger when an element appears mid-tap. The peek "Shop Now" pill
  // materialises right where many tile taps land, so without a guard the
  // sheet would instantly expand and skip the peek state. Block expand
  // calls for a short window after the drawer opens.
  var justOpenedAt = 0;
  function expandProductSheet(root) {
    if (!root) return;
    if (Date.now() - justOpenedAt < 450) return; // ghost-click guard
    root.setAttribute('data-product-open', '');
    // Mute the video while the product sheet covers it — the user is now
    // reading the card, not the clip.
    var video = $(root, '[data-cg-ugc-drawer-video]');
    var muteBtn = $(root, '[data-cg-ugc-drawer-mute]');
    if (video && !video.muted) {
      video.muted = true;
      if (muteBtn) syncMuteIcon(muteBtn, true);
    }
  }

  function collapseProductSheet(root) {
    if (!root) return;
    root.removeAttribute('data-product-open');
  }

  function resetProductSide(root) {
    if (!root) return;
    var track = $(root, '[data-cg-ugc-drawer-gallery-track]');
    var dots = $(root, '[data-cg-ugc-drawer-gallery-dots]');
    var variantsEl = $(root, '[data-cg-ugc-drawer-variants]');
    var titleEl = $(root, '[data-cg-ugc-drawer-title]');
    var priceEl = $(root, '[data-cg-ugc-drawer-price]');
    var descEl = $(root, '[data-cg-ugc-drawer-desc]');
    var freqList = $(root, '[data-cg-ugc-drawer-frequency-list]');
    var subToggle = $(root, '[data-cg-ugc-drawer-subscribe-toggle]');
    var subState = $(root, '[data-cg-ugc-drawer-subscribe-state]');
    var purchase = root.querySelector('.hero-general__purchase');
    clear(track);
    clear(dots);
    clear(variantsEl);
    if (freqList) clear(freqList);
    if (subToggle) subToggle.hidden = true;
    if (subState) subState.hidden = true;
    if (purchase) purchase.classList.remove('is-subscribe');
    safeText(titleEl, '');
    safeText(priceEl, '');
    if (descEl) descEl.innerHTML = '';
    // Reset the mobile peek bar
    var peek = $(root, '[data-cg-ugc-drawer-peek]');
    if (peek) {
      peek.hidden = true;
      var peekImg = $(root, '[data-cg-ugc-drawer-peek-image]');
      var peekPrice = $(root, '[data-cg-ugc-drawer-peek-price]');
      var peekTitle = $(root, '[data-cg-ugc-drawer-peek-title]');
      if (peekImg) peekImg.removeAttribute('src');
      if (peekPrice) peekPrice.textContent = '';
      if (peekTitle) peekTitle.textContent = '';
    }
    state.galleryIndex = 0;
    state.galleryImages = [];
    state.selectedOptions = [];
    state.sellingPlans = [];
    state.subMode = false;
    state.sellingPlanId = '';
  }

  // ------------------------------------------------------------------
  // Prev / next (loops between tiles)
  // ------------------------------------------------------------------
  function getTriggersForSection(sectionId) {
    if (!sectionId) return [];
    if (triggerCache[sectionId]) return triggerCache[sectionId];
    var section =
      document.querySelector('[data-section-id="' + sectionId + '"]') ||
      document.getElementById(sectionId) ||
      document.querySelector('[data-cg-ugc][data-section-id="' + sectionId + '"]');
    if (!section) {
      section = document.querySelector('[data-cg-ugc]');
    }
    if (!section) return [];
    var list = Array.prototype.slice.call(section.querySelectorAll('[data-cg-ugc-open]'));
    triggerCache[sectionId] = list;
    return list;
  }

  function detailFromTrigger(trigger, sectionId, index) {
    return {
      sectionId: sectionId,
      tileIndex: index,
      videoSrc: trigger.getAttribute('data-cg-ugc-video-src') || trigger.getAttribute('data-video-src') || '',
      posterSrc: trigger.getAttribute('data-cg-ugc-poster-src') || '',
      creator: trigger.getAttribute('data-cg-ugc-creator') || '',
      caption: trigger.getAttribute('data-cg-ugc-caption') || trigger.getAttribute('data-caption') || '',
      productHandle: trigger.getAttribute('data-cg-ugc-product-handle') || ''
    };
  }

  function navigate(dir) {
    if (!state.detail) return;
    var triggers = getTriggersForSection(state.detail.sectionId);
    if (!triggers.length) return;

    var current = state.detail.tileIndex;
    if (typeof current !== 'number' || isNaN(current)) current = 0;
    var next = (current + dir + triggers.length) % triggers.length;
    var trigger = triggers[next];
    if (!trigger) return;

    var newDetail = detailFromTrigger(trigger, state.detail.sectionId, next);
    document.dispatchEvent(new CustomEvent('cg-ugc:open-drawer', { detail: newDetail }));
  }

  // ------------------------------------------------------------------
  // Product fetch + render
  // ------------------------------------------------------------------
  function loadProduct(handle, root) {
    if (!handle) return;
    if (productCache.has(handle)) {
      renderProduct(productCache.get(handle), root);
      return;
    }
    var url = '/products/' + encodeURIComponent(handle) + '.js';
    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('product fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (product) {
        productCache.set(handle, product);
        if (state.detail && state.detail.productHandle === handle) {
          renderProduct(product, root);
        }
      })
      .catch(function (err) {
        console.warn('[cg-ugc-drawer] could not load product', handle, err);
      });
  }

  function renderProduct(product, root) {
    state.product = product;

    var titleEl = $(root, '[data-cg-ugc-drawer-title]');
    var priceEl = $(root, '[data-cg-ugc-drawer-price]');
    var variantsEl = $(root, '[data-cg-ugc-drawer-variants]');
    var descEl = $(root, '[data-cg-ugc-drawer-desc]');
    var atcBtn = $(root, '[data-cg-ugc-drawer-atc]');

    // Title
    safeText(titleEl, product.title || '');

    // Pick initial variant (first available, else first)
    var initialVariant = pickInitialVariant(product);

    // Price — populate textContent. Use variant price if available, else product.price.
    var priceCents = initialVariant ? initialVariant.price : product.price;
    if (priceEl) {
      priceEl.textContent = formatMoney(priceCents);
    }

    // Populate the mobile peek bar (thumbnail + price + name).
    var peek = $(root, '[data-cg-ugc-drawer-peek]');
    if (peek) {
      var peekImg = $(root, '[data-cg-ugc-drawer-peek-image]');
      var peekPrice = $(root, '[data-cg-ugc-drawer-peek-price]');
      var peekTitle = $(root, '[data-cg-ugc-drawer-peek-title]');
      var thumb = product.featured_image
        || (product.images && product.images.length ? product.images[0] : '');
      if (peekImg) {
        peekImg.src = thumb || '';
        peekImg.alt = product.title || '';
      }
      if (peekPrice) peekPrice.textContent = formatMoney(priceCents);
      if (peekTitle) peekTitle.textContent = product.title || '';
      peek.hidden = false;
    }

    // Description — populated but CSS hides by default
    if (descEl) {
      descEl.innerHTML = product.body_html || product.description || '';
    }

    // Gallery — one-at-a-time
    renderGallery(root, product);

    // Initial selected options from the chosen variant
    if (initialVariant && Array.isArray(initialVariant.options)) {
      state.selectedOptions = initialVariant.options.slice();
    } else {
      state.selectedOptions = [];
    }

    // Variants — per-option pills with short value labels
    renderVariants(variantsEl, product, root);

    // ATC initial state
    if (atcBtn) {
      if (initialVariant) {
        state.variantId = String(initialVariant.id);
        atcBtn.dataset.variantId = state.variantId;
        atcBtn.disabled = !initialVariant.available;
      } else {
        state.variantId = '';
        atcBtn.removeAttribute('data-variant-id');
        atcBtn.disabled = true;
      }
    }

    // Subscribe & Save — only rendered when the product exposes selling plans.
    // Hero parity: shopping bag SVG + plan name + (optional) "Save N%" pill.
    renderSellingPlans(root, product);
  }

  function pickInitialVariant(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return product.variants[i];
    }
    return product.variants[0];
  }

  // ------------------------------------------------------------------
  // Gallery — one-at-a-time pattern
  // ------------------------------------------------------------------
  // Gallery — Hero General scroll-snap pattern. Slides are .hero-general__gallery-slide
  // children of the scroll track; dots are .hero-general__gallery-dot buttons.
  function renderGallery(root, product) {
    var galleryWrap = $(root, '[data-cg-ugc-drawer-gallery]');
    var track = $(root, '[data-cg-ugc-drawer-gallery-track]');
    var dotsEl = $(root, '[data-cg-ugc-drawer-gallery-dots]');
    var prevBtn = $(root, '[data-cg-ugc-drawer-gallery-prev]');
    var nextBtn = $(root, '[data-cg-ugc-drawer-gallery-next]');

    if (!track) return;
    clear(track);
    if (dotsEl) clear(dotsEl);

    var images = (product.images && product.images.length) ? product.images.slice() : [];
    if (!images.length && product.featured_image) {
      images = [product.featured_image];
    }

    state.galleryImages = images;
    state.galleryIndex = 0;

    if (!images.length) {
      if (galleryWrap) galleryWrap.hidden = true;
      return;
    }
    if (galleryWrap) galleryWrap.hidden = false;

    images.forEach(function (src, idx) {
      var slide = document.createElement('div');
      slide.className = 'hero-general__gallery-slide';
      slide.dataset.index = String(idx);
      var img = document.createElement('img');
      img.src = src;
      img.alt = product.title || '';
      img.loading = idx === 0 ? 'eager' : 'lazy';
      slide.appendChild(img);
      track.appendChild(slide);
    });

    if (dotsEl) {
      images.forEach(function (_src, idx) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'hero-general__gallery-dot';
        dot.dataset.heroGalleryDot = String(idx);
        dot.setAttribute('aria-label', 'View image ' + (idx + 1));
        if (idx === 0) dot.classList.add('is-active');
        dot.addEventListener('click', function () { setGalleryIndex(idx); });
        dotsEl.appendChild(dot);
      });
    }

    var multi = images.length > 1;
    if (prevBtn) prevBtn.hidden = !multi;
    if (nextBtn) nextBtn.hidden = !multi;
    if (dotsEl) dotsEl.hidden = !multi;

    track.scrollLeft = 0;
    wireGalleryScroll(track, dotsEl);
  }

  function wireGalleryScroll(track, dotsEl) {
    if (track._cgUgcScrollWired) return;
    track._cgUgcScrollWired = true;
    var t;
    track.addEventListener('scroll', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var first = track.querySelector('.hero-general__gallery-slide');
        if (!first) return;
        var w = first.offsetWidth;
        if (!w) return;
        var idx = Math.round(track.scrollLeft / w);
        if (idx === state.galleryIndex) return;
        state.galleryIndex = idx;
        if (dotsEl) {
          Array.prototype.forEach.call(dotsEl.children, function (child, i) {
            child.classList.toggle('is-active', i === idx);
          });
        }
      }, 60);
    });
  }

  function setGalleryIndex(idx) {
    var root = state.rootEl;
    if (!root) return;
    var len = state.galleryImages.length;
    if (!len) return;
    var next = ((idx % len) + len) % len;
    state.galleryIndex = next;

    var track = $(root, '[data-cg-ugc-drawer-gallery-track]');
    var dotsEl = $(root, '[data-cg-ugc-drawer-gallery-dots]');
    if (track) {
      var slides = track.children;
      if (slides[next]) {
        var left = slides[next].offsetLeft;
        try { track.scrollTo({ left: left, behavior: 'smooth' }); }
        catch (_) { track.scrollLeft = left; }
      }
    }
    if (dotsEl) {
      Array.prototype.forEach.call(dotsEl.children, function (child, i) {
        child.classList.toggle('is-active', i === next);
      });
    }
  }

  function stepGallery(dir) {
    if (!state.galleryImages.length) return;
    setGalleryIndex(state.galleryIndex + dir);
  }

  // ------------------------------------------------------------------
  // Variants — render per-option pills with short value labels
  // ------------------------------------------------------------------
  function renderVariants(variantsEl, product, root) {
    if (!variantsEl) return;
    clear(variantsEl);

    if (!product.variants || product.variants.length <= 1) {
      variantsEl.hidden = true;
      return;
    }
    variantsEl.hidden = false;

    // product.options is an array of option names: ["Size", "Color"]
    var optionNames = Array.isArray(product.options) ? product.options.slice() : [];

    // Derive unique values per option position from variants
    var optionValues = [];
    var optionsCount = 0;
    if (product.variants[0] && Array.isArray(product.variants[0].options)) {
      optionsCount = product.variants[0].options.length;
    } else {
      optionsCount = optionNames.length;
    }

    if (optionsCount === 0) {
      // Fallback: render full title pills (shouldn't happen for products w/ variants)
      renderVariantTitleFallback(variantsEl, product, root);
      return;
    }

    for (var pos = 0; pos < optionsCount; pos++) {
      var seen = {};
      var vals = [];
      product.variants.forEach(function (v) {
        var val = v.options && v.options[pos];
        if (val == null) return;
        if (!seen[val]) {
          seen[val] = true;
          vals.push(val);
        }
      });
      optionValues.push(vals);
    }

    // Hero parity: single-option products use a FLAT segmented selector —
    // variant buttons go directly inside [data-cg-ugc-drawer-variants] with
    // no per-option group wrapper or label. This matches the hero's
    // .hero-general__variant-selector grid look.
    if (optionsCount === 1) {
      var values = optionValues[0] || [];
      values.forEach(function (value) {
        var btn = buildVariantButton(product, 0, value, root, true);
        variantsEl.appendChild(btn);
      });
      return;
    }

    // Multi-option (rare for Cow Guys) — keep grouped layout with labels.
    optionValues.forEach(function (values, pos) {
      var group = document.createElement('div');
      group.className = 'cg-ugc-drawer__variant-group';
      group.dataset.optionPosition = String(pos);

      var label = document.createElement('span');
      label.className = 'cg-ugc-drawer__variant-label';
      label.textContent = optionNames[pos] || ('Option ' + (pos + 1));
      group.appendChild(label);

      var pills = document.createElement('div');
      pills.className = 'cg-ugc-drawer__variant-pills';

      values.forEach(function (value) {
        var btn = buildVariantButton(product, pos, value, root, false);
        pills.appendChild(btn);
      });

      group.appendChild(pills);
      variantsEl.appendChild(group);
    });
  }

  // Build a single variant button — shared by flat (single-option) and
  // grouped (multi-option) renderers.
  function buildVariantButton(product, pos, value, root, useFullTitle) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cg-ugc-drawer__variant hero-general__variant-btn';
    btn.dataset.optionPosition = String(pos);
    btn.dataset.optionValue = value;

    // Match the matched variant so we can store its id + price on the button —
    // mirrors the hero's [data-hero-variant-id] / [data-hero-variant-price]
    // pattern (used by updateSubDiscounts to look up combo discounts).
    var matched = product.variants.filter(function (v) {
      return v.options && v.options[pos] === value;
    });
    var primary = matched.length ? matched[0] : null;
    if (primary) {
      btn.dataset.heroVariantId = String(primary.id);
      btn.setAttribute('data-hero-variant-id', String(primary.id));
      btn.dataset.heroVariantPrice = formatMoney(primary.price);
      btn.setAttribute('data-hero-variant-price', formatMoney(primary.price));
    }

    var labelText = value;
    if (useFullTitle && primary && primary.title) {
      labelText = primary.title;
    }
    setVariantLabel(btn, labelText);
    appendSaveTag(btn, primary, root);

    var anyAvail = product.variants.some(function (v) {
      return v.options && v.options[pos] === value && v.available;
    });
    if (!anyAvail) {
      btn.classList.add('is-sold-out');
      btn.setAttribute('aria-disabled', 'true');
    }

    if (state.selectedOptions[pos] === value) {
      btn.classList.add('is-selected', 'is-active');
      btn.setAttribute('aria-pressed', 'true');
    } else {
      btn.setAttribute('aria-pressed', 'false');
    }

    btn.addEventListener('click', function () {
      handleOptionPick(pos, value, root);
    });

    return btn;
  }

  function renderVariantTitleFallback(variantsEl, product, root) {
    // Fallback for products w/ no options array — show only the last
    // option-position value (clean short string), not full variant.title.
    product.variants.forEach(function (variant) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cg-ugc-drawer__variant';
      btn.dataset.variantId = String(variant.id);
      var label;
      if (variant.options && variant.options.length) {
        label = variant.options[variant.options.length - 1];
      } else {
        label = variant.title || ('#' + variant.id);
      }
      setVariantLabel(btn, label);
      appendSaveTag(btn, variant, root);
      if (!variant.available) {
        btn.classList.add('is-sold-out');
        btn.setAttribute('aria-disabled', 'true');
      }
      btn.classList.add('hero-general__variant-btn');
      btn.addEventListener('click', function () {
        selectVariant(variant, root);
        Array.prototype.forEach.call(
          variantsEl.querySelectorAll('[data-variant-id]'),
          function (el) {
            var match = (el === btn);
            el.classList.toggle('is-selected', match);
            el.classList.toggle('is-active', match);
            el.setAttribute('aria-pressed', match ? 'true' : 'false');
          }
        );
      });
      variantsEl.appendChild(btn);
    });
  }

  function handleOptionPick(position, value, root) {
    state.selectedOptions[position] = value;

    // Find variant matching ALL currently selected options
    var product = state.product;
    if (!product || !product.variants) return;

    var match = product.variants.find(function (v) {
      if (!v.options) return false;
      for (var i = 0; i < state.selectedOptions.length; i++) {
        if (state.selectedOptions[i] == null) continue;
        if (v.options[i] !== state.selectedOptions[i]) return false;
      }
      return true;
    });

    // If no full match (e.g., other options not yet picked), fall back to first
    // variant matching at this position
    if (!match) {
      match = product.variants.find(function (v) {
        return v.options && v.options[position] === value;
      });
      if (match && Array.isArray(match.options)) {
        state.selectedOptions = match.options.slice();
      }
    }

    // Refresh pill selected states
    var variantsEl = $(root, '[data-cg-ugc-drawer-variants]');
    if (variantsEl) {
      Array.prototype.forEach.call(
        variantsEl.querySelectorAll('.cg-ugc-drawer__variant'),
        function (btn) {
          var pos = parseInt(btn.dataset.optionPosition, 10);
          var val = btn.dataset.optionValue;
          var isSelected = !isNaN(pos) && state.selectedOptions[pos] === val;
          btn.classList.toggle('is-selected', isSelected);
          btn.classList.toggle('is-active', isSelected);
          btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        }
      );
    }

    if (match) selectVariant(match, root);
  }

  function selectVariant(variant, root) {
    if (!variant) return;
    state.variantId = String(variant.id);

    var atcBtn = $(root, '[data-cg-ugc-drawer-atc]');
    var priceEl = $(root, '[data-cg-ugc-drawer-price]');

    if (atcBtn) {
      atcBtn.dataset.variantId = state.variantId;
      atcBtn.disabled = !variant.available;
    }
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }

    // Subscribe mode: recompute the subscribed price + each pill's combined
    // discount. Mirrors hero's updateHeroPrice + updateSubDiscounts.
    if (state.subMode) {
      updateSubscriptionPrice(root, variant);
    }
    updateSubDiscounts(root, variant);

    // If the variant has a featured_image and it matches one of the gallery
    // images, jump the gallery to that index (don't replace).
    if (variant.featured_image && variant.featured_image.src && state.galleryImages.length) {
      var target = variant.featured_image.src;
      var idx = -1;
      for (var i = 0; i < state.galleryImages.length; i++) {
        if (state.galleryImages[i] === target ||
            (state.galleryImages[i] && state.galleryImages[i].indexOf(stripQuery(target)) !== -1) ||
            (target && target.indexOf(stripQuery(state.galleryImages[i])) !== -1)) {
          idx = i;
          break;
        }
      }
      if (idx >= 0) setGalleryIndex(idx);
    }
  }

  function stripQuery(url) {
    if (!url) return '';
    var q = url.indexOf('?');
    return q >= 0 ? url.substring(0, q) : url;
  }

  // ------------------------------------------------------------------
  // Subscribe & Save (Hero parity)
  // ------------------------------------------------------------------
  // Bag SVG used in hero's frequency pills and ATC submit. Stroke
  // currentColor / width 2 / fill none / viewBox 0 0 24 24.
  function bagSvg() {
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
      '<path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>' +
      '<line x1="3" y1="6" x2="21" y2="6"/>' +
      '<path d="M16 10a4 4 0 01-8 0"/>' +
      '</svg>';
  }

  // Flatten product.selling_plan_groups[*].selling_plans[*] into
  // { id, name, pct } where pct is the percentage discount value when the
  // first price_adjustment is a percentage. Returns [] when none.
  function extractSellingPlans(product) {
    var out = [];
    if (!product || !Array.isArray(product.selling_plan_groups)) return out;
    product.selling_plan_groups.forEach(function (group) {
      if (!group || !Array.isArray(group.selling_plans)) return;
      group.selling_plans.forEach(function (plan) {
        if (!plan || plan.id == null) return;
        var pct = 0;
        if (Array.isArray(plan.price_adjustments) && plan.price_adjustments.length) {
          var adj = plan.price_adjustments[0];
          if (adj && adj.value_type === 'percentage' && Number(adj.value) > 0) {
            pct = Number(adj.value);
          }
        }
        out.push({ id: String(plan.id), name: plan.name || '', pct: pct });
      });
    });
    return out;
  }

  // Parse the embedded "(N% OFF)" out of a variant title — the hero uses
  // this as the variant-tier discount which then stacks with the plan
  // percentage. Returns 0 if not present.
  function variantPctFromTitle(variant) {
    if (!variant || !variant.title) return 0;
    var m = String(variant.title).match(/(\d+(?:\.\d+)?)\s*%\s*OFF/i);
    if (!m) return 0;
    var pct = parseFloat(m[1]);
    return isNaN(pct) ? 0 : pct;
  }

  // Look up the allocation price (in cents) for a given variant + plan id
  // via product.variants[i].selling_plan_allocations[*].per_delivery_price.
  function findAllocationPrice(product, variantId, planId) {
    if (!product || !Array.isArray(product.variants)) return null;
    var vid = String(variantId);
    var pid = String(planId);
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (String(v.id) !== vid) continue;
      var allocs = v.selling_plan_allocations;
      if (!Array.isArray(allocs)) return null;
      for (var j = 0; j < allocs.length; j++) {
        var a = allocs[j];
        if (!a || !a.selling_plan_id) continue;
        if (String(a.selling_plan_id) !== pid) continue;
        if (a.per_delivery_price != null) return a.per_delivery_price;
        if (a.price != null) return a.price;
        return null;
      }
      return null;
    }
    return null;
  }

  function renderSellingPlans(root, product) {
    var subToggle = $(root, '[data-cg-ugc-drawer-subscribe-toggle]');
    var subState = $(root, '[data-cg-ugc-drawer-subscribe-state]');
    var freqList = $(root, '[data-cg-ugc-drawer-frequency-list]');
    var onetimeToggle = $(root, '[data-cg-ugc-drawer-onetime-toggle]');

    var plans = extractSellingPlans(product);
    state.sellingPlans = plans;

    if (!plans.length) {
      if (subToggle) subToggle.hidden = true;
      if (subState) subState.hidden = true;
      if (freqList) clear(freqList);
      return;
    }

    if (subToggle) subToggle.hidden = false;
    if (subState) subState.hidden = false;
    if (freqList) {
      clear(freqList);
      plans.forEach(function (plan) {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'hero-general__frequency-pill';
        pill.setAttribute('data-hero-plan-id', plan.id);
        pill.setAttribute('data-cg-ugc-drawer-plan', plan.id);

        // Bag SVG (matches the ATC bag — same 3-path glyph).
        pill.insertAdjacentHTML('beforeend', bagSvg());

        // Plan name span.
        var nameSpan = document.createElement('span');
        nameSpan.className = 'hero-general__pill-name';
        nameSpan.textContent = plan.name;
        pill.appendChild(nameSpan);

        if (plan.pct > 0) {
          var pctSpan = document.createElement('span');
          pctSpan.className = 'hero-general__pill-discount';
          pctSpan.setAttribute('data-hero-pill-discount', '');
          pctSpan.setAttribute('data-plan-pct', String(plan.pct));
          pctSpan.textContent = 'Save ' + plan.pct + '%';
          pill.appendChild(pctSpan);
        }

        pill.addEventListener('click', function () {
          handlePlanPick(pill, plan, root);
        });

        freqList.appendChild(pill);
      });
    }

    // Subscribe toggle: enter subscribe state.
    if (subToggle && !subToggle._cgUgcWired) {
      subToggle._cgUgcWired = true;
      subToggle.addEventListener('click', function () {
        enterSubscribeMode(root);
      });
    }

    // Back-link: leave subscribe state.
    if (onetimeToggle && !onetimeToggle._cgUgcWired) {
      onetimeToggle._cgUgcWired = true;
      onetimeToggle.addEventListener('click', function () {
        exitSubscribeMode(root);
      });
    }

    // Once pills exist, refresh the discount labels against the current
    // variant in case it has its own variant-title "(N% OFF)" stacking.
    var currentVariant = findVariantById(product, state.variantId);
    if (currentVariant) updateSubDiscounts(root, currentVariant);
  }

  function findVariantById(product, variantId) {
    if (!product || !Array.isArray(product.variants) || !variantId) return null;
    var vid = String(variantId);
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === vid) return product.variants[i];
    }
    return null;
  }

  function enterSubscribeMode(root) {
    if (!state.sellingPlans.length) return;
    var purchase = root.querySelector('.hero-general__purchase');
    if (purchase) purchase.classList.add('is-subscribe');
    state.subMode = true;
    // No pill auto-highlight — each pill is its own add-to-cart trigger,
    // so visually selecting one before the user has clicked anything is
    // confusing. Clear any prior selection.
    state.sellingPlanId = '';
    var freqList = $(root, '[data-cg-ugc-drawer-frequency-list]');
    if (freqList) {
      Array.prototype.forEach.call(
        freqList.querySelectorAll('.hero-general__frequency-pill'),
        function (pill) {
          pill.classList.remove('is-selected', 'is-active');
          pill.setAttribute('aria-pressed', 'false');
        }
      );
    }
  }

  function exitSubscribeMode(root) {
    var purchase = root.querySelector('.hero-general__purchase');
    if (purchase) purchase.classList.remove('is-subscribe');
    state.subMode = false;
    state.sellingPlanId = '';
    // Restore one-time price.
    var priceEl = $(root, '[data-cg-ugc-drawer-price]');
    var variant = findVariantById(state.product, state.variantId);
    if (priceEl && variant) priceEl.textContent = formatMoney(variant.price);
  }

  function handlePlanPick(pillEl, plan, root) {
    // Each frequency pill IS its own add-to-cart trigger — same as the
    // hero's frequency-pill pattern (each pill is a <button type="submit">
    // inside its own product-form). One click → adds the variant + this
    // plan to cart, no extra Buy Now button needed.
    state.sellingPlanId = plan.id;
    addToCartWithPlan(pillEl, plan.id);
  }

  // Recompute the live subscribed price using selling_plan_allocations
  // (the JSON equivalent of window.heroSellingPlanData).
  function updateSubscriptionPrice(root, variant) {
    if (!state.subMode || !state.sellingPlanId) return;
    var priceEl = $(root, '[data-cg-ugc-drawer-price]');
    if (!priceEl || !variant) return;
    var cents = findAllocationPrice(state.product, variant.id, state.sellingPlanId);
    if (cents != null) {
      priceEl.textContent = formatMoney(cents);
    } else {
      // Fallback: apply plan pct to variant price.
      var plan = state.sellingPlans.find(function (p) { return p.id === state.sellingPlanId; });
      if (plan && plan.pct > 0) {
        var discounted = Math.round(variant.price * (1 - plan.pct / 100));
        priceEl.textContent = formatMoney(discounted);
      } else {
        priceEl.textContent = formatMoney(variant.price);
      }
    }
  }

  // Stack variant-title "(N% OFF)" with plan pct, mirroring hero's
  // window.heroComboDiscounts. Combined % = 1 - (1 - vPct)(1 - pPct).
  function updateSubDiscounts(root, variant) {
    if (!root || !variant) return;
    var pills = root.querySelectorAll('[data-hero-pill-discount]');
    if (!pills || !pills.length) return;
    var variantPct = variantPctFromTitle(variant);
    Array.prototype.forEach.call(pills, function (span) {
      var planPctAttr = span.getAttribute('data-plan-pct');
      var planPct = planPctAttr ? parseFloat(planPctAttr) : 0;
      if (isNaN(planPct)) planPct = 0;
      var combined;
      if (variantPct > 0 && planPct > 0) {
        var stacked = 1 - (1 - variantPct / 100) * (1 - planPct / 100);
        combined = Math.round(stacked * 100);
      } else {
        combined = Math.max(variantPct, planPct);
      }
      if (combined > 0) {
        span.textContent = 'Save ' + combined + '%';
      }
    });
  }

  // Variant labels in Cow Guys' catalog use " | " to pack discount/marketing
  // copy into the option value itself, e.g. "3 Bottles | Family Favorite | 20% OFF".
  // Match the Hero General pattern: render each pipe segment on its own line
  // so the segmented selector shows quantity, label, and discount stacked.
  function cleanLabel(value) {
    if (value == null) return '';
    return String(value)
      .split('|')
      .map(function (s) { return s.trim(); })
      .filter(function (s) { return s.length > 0; })
      .join('\n');
  }
  function setVariantLabel(btn, value) {
    // New product-card standard (matches the hero pills): first pipe
    // segment is the label, later segments render as small sublabels,
    // and any segment containing '%' is hidden — the savings tag from
    // appendSaveTag replaces it.
    var parts = cleanLabel(value).split('\n');
    var html = '';
    parts.forEach(function (line, idx) {
      if (idx > 0 && line.indexOf('%') !== -1) return;
      var safe = line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      var cls = idx === 0
        ? 'cg-ugc-drawer__vline'
        : 'hero-general__variant-sub';
      html += '<span class="' + cls + '">' + safe + '</span>';
    });
    btn.innerHTML = html;
  }

  // Savings percent for a variant: compare-at price preferred, else the
  // "(NN%)" convention in the variant title. Rounded to the nearest 5,
  // matching the hero pills and the bundle strip.
  function savePercent(variant) {
    if (!variant) return 0;
    var pct = 0;
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      pct = ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price;
    } else if (variant.title && variant.title.indexOf('%') !== -1) {
      var tail = variant.title.split('(').pop();
      if (tail.indexOf('%') !== -1) {
        var n = parseFloat(tail.split('%')[0]);
        if (!isNaN(n)) pct = n;
      }
    }
    return Math.round(pct / 5) * 5;
  }

  function appendSaveTag(btn, variant, root) {
    var pct = savePercent(variant);
    if (pct <= 0) return;
    var host = root && root.querySelector('[data-cg-ugc-drawer-variants]');
    var word = (host && host.getAttribute('data-save-text')) || 'Save';
    var tag = document.createElement('span');
    tag.className = 'hero-general__variant-save';
    tag.textContent = word + ' ' + pct + '%';
    btn.appendChild(tag);
  }

  // ------------------------------------------------------------------
  // Add to cart
  // ------------------------------------------------------------------
  // Routes through the shared helper. When in subscribe mode the helper
  // is called with the active selling_plan id so the cart item gets
  // attached to the subscription.
  function addToCart(atcBtn) {
    var planId = state.subMode ? state.sellingPlanId : '';
    submitCartAdd(atcBtn, planId);
  }

  function addToCartWithPlan(triggerEl, planId) {
    submitCartAdd(triggerEl, planId);
  }

  function submitCartAdd(triggerEl, planId) {
    var variantId = state.variantId ||
      (triggerEl && triggerEl.dataset && triggerEl.dataset.variantId);
    if (!variantId) return;

    var labelEl = triggerEl && triggerEl.querySelector
      ? triggerEl.querySelector('[data-cg-ugc-drawer-atc-label]')
      : null;
    var originalLabel = labelEl
      ? labelEl.textContent
      : (triggerEl ? triggerEl.textContent : '');
    if (triggerEl) {
      triggerEl.disabled = true;
      triggerEl.classList.add('is-loading');
    }

    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var sectionsToRender = ['cart-notification', 'cart-drawer', 'cart-icon-bubble'];

    var item = { id: Number(variantId), quantity: 1 };
    if (planId) item.selling_plan = Number(planId) || planId;

    var body = JSON.stringify({
      items: [item],
      sections: sectionsToRender,
      sections_url: window.location.pathname
    });

    fetch(rootPath + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript'
      },
      body: body
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (triggerEl) {
          triggerEl.classList.remove('is-loading');
          triggerEl.classList.add('is-success');
          if (labelEl) labelEl.textContent = 'Added';
        }
        // Close the UGC drawer first, then wait for two paint frames
        // before triggering the cart drawer. Running renderContents on
        // the same frame as the dialog close means the heavy DOM swap
        // and the dialog's closing paint compete, which is what felt
        // like "the cart opens before the popup exits and lags."
        try { closeDrawer(); } catch (_) {}
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            handleCartResponse(data);
          });
        });
      })
      .catch(function (err) {
        console.error('[cg-ugc-drawer] add to cart failed', err);
        if (triggerEl) {
          triggerEl.classList.remove('is-loading');
          triggerEl.disabled = false;
        }
        window.location.href = '/cart';
      });
  }

  // ------------------------------------------------------------------
  // Boot
  // ------------------------------------------------------------------
  function boot() {
    document.addEventListener('cg-ugc:open-drawer', function (event) {
      var detail = event && event.detail ? event.detail : {};
      openDrawer(detail);
    });

    // Re-init on section reload in the theme editor
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', function () {
        triggerCache = {};
        state.rootEl = null;
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
